/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { getCtoCandidates, createCtoCandidate, updateCtoCandidate, deleteCtoCandidate } from '../utils/api';
import { canHover, isTouchDevice } from '../hooks/useBreakpoint';
import useAutoRefresh, { isSameData } from '../hooks/useAutoRefresh';
import { SCORE_TOOLTIP, getScoreColor, getAction, ACTION_COLORS, getSuggestedScore } from '../utils/ctoShared';
import KpiCards from './KpiCards';
import CTOEditDrawer from './CTOEditDrawer';

const COLUMNS = [
  { key: 'linkedin_url',        label: 'LinkedIn URL',                       type: 'link' },
  { key: 'current_title',       label: 'Current Title',                      type: 'text' },
  { key: 'years_experience',    label: 'Years Experience (7+ preferred)',    type: 'number' },
  { key: 'backend_focus',       label: 'Backend Focus',                      type: 'checkbox' },
  { key: 'system_architecture', label: 'System / Architecture Experience',   type: 'checkbox' },
  { key: 'relevant_tech',       label: 'Relevant Tech',                      type: 'text' },
  { key: 'company_type',        label: 'Company Type',                       type: 'text' },
  { key: 'leadership_signals',  label: 'Leadership Signals',                 type: 'text' },
  { key: 'notes',               label: 'Notes',                              type: 'text' },
  { key: 'red_flags',           label: 'Red Flags',                          type: 'text' },
  { key: 'score',               label: 'Score (1–5)',                        type: 'score' },
  { key: 'action',              label: 'Action',                             type: 'action' },
  { key: 'contact_method',      label: 'Contact Method',                    type: 'select' },
  { key: 'contact_date',        label: 'Contact Date',                      type: 'date' },
  { key: 'response',            label: 'Response',                           type: 'text' },
  { key: 'response_date',       label: 'Response Date',                     type: 'date' },
  { key: 'next_step',           label: 'Next Step',                          type: 'text' },
  { key: 'closed',              label: 'Closed (Y/N)',                       type: 'checkbox' },
];

const getColumnWidth = (key) => {
  switch (key) {
    case 'current_title':
    case 'company_type':
      return { minWidth: 300, maxWidth: 300 };
    case 'relevant_tech':
    case 'system_architecture':
      return { minWidth: 450, maxWidth: 450 };
    case 'leadership_signals':
      return { minWidth: 500, maxWidth: 500 };
    case 'notes':
      return { minWidth: 650, maxWidth: 650 };
    case 'red_flags':
      return { minWidth: 600, maxWidth: 600 };
    case 'response':
    case 'next_step':
      return { minWidth: 500, maxWidth: 500 };
    default:
      return {};
  }
};

// DATE values are shown from their YYYY-MM-DD part so the browser timezone can't shift the day.
const fmtDay = (v) => {
  if (!v) return null;
  const s = String(v).split('T')[0];
  try { return format(parseISO(s), 'MMM d, yyyy'); } catch { return s; }
};

// One test each for the Contacted and Closed cards, so the card count and the list always match.
const isContacted = (c) => !!(c.contact_date || c.contact_method);
const isClosed    = (c) => !!c.closed;

export default function CTOTracker({ showToast, refreshKey, onRefreshed }) {
  const [candidates,  setCandidates]  = useState([]);
  const [loading,      setLoading]    = useState(true);
  const [editId,       setEditId]      = useState(null);
  const [filter,       setFilter]      = useState('TOP'); // ALL | ACTIVE | TOP | CONTACTED | CLOSED
  const [search,       setSearch]      = useState('');
  const [scoreFilter,  setScoreFilter] = useState('');
  const [newName,      setNewName]     = useState('');
  const [creating,     setCreating]    = useState(false);
  const [selected,     setSelected]    = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const scrollRef = useRef();

  const toast = showToast || (() => {});

  const loadCandidates = useCallback(async () => {
    try {
      setCandidates(await getCtoCandidates());
    } catch {
      toast('Failed to load candidates', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  // Background refresh: no spinner, no toast, state only changes when the data did. Selection
  // is pruned to ids that still exist by the visible-rows effect below.
  const silentRefresh = useCallback(async (shouldApply) => {
    const data = await getCtoCandidates();
    if (!shouldApply()) return;
    setCandidates(prev => (isSameData(prev, data) ? prev : data));
  }, []);

  // Paused while the edit drawer is open or a create / delete is in flight.
  const { refreshNow } = useAutoRefresh(silentRefresh, {
    paused: editId !== null || creating || bulkDeleting,
    onRefreshed,
  });

  // Header Refresh button.
  const lastRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (lastRefreshKey.current === refreshKey) return;
    lastRefreshKey.current = refreshKey;
    refreshNow().catch(() => toast('Failed to load candidates', 'error'));
  }, [refreshKey, refreshNow, toast]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const rec = await createCtoCandidate({ candidate_name: name });
      setCandidates(prev => [rec, ...prev]);
      setNewName('');
      toast(`Added "${name}"`);
      setEditId(rec.id);
    } catch {
      toast('Failed to add candidate', 'error');
    } finally {
      setCreating(false);
    }
  };

  // Deletes without asking; callers confirm first. Throws on failure so the drawer can stay open.
  const removeCandidate = async (id) => {
    try {
      await deleteCtoCandidate(id);
    } catch (e) {
      toast('Delete failed', 'error');
      throw e;
    }
    setCandidates(prev => prev.filter(c => c.id !== id));
    setSelected(prev => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n; });
    setEditId(prev => (prev === id ? null : prev));
    toast('Candidate deleted');
  };

  const handleRowDelete = async (id) => {
    if (!window.confirm('Delete this candidate? This cannot be undone.')) return;
    try { await removeCandidate(id); } catch { /* toast already shown */ }
  };

  const handleSave = async (id, data) => {
    try {
      const updated = await updateCtoCandidate(id, data);
      setCandidates(prev => prev.map(c => c.id === id ? updated : c));
      setEditId(null);
      toast('✓ Saved');
    } catch (e) {
      if (e.response?.status === 409) {
        toast(e.response.data.message, 'warning');
        loadCandidates();
      } else if (e.response?.status === 400) toast(e.response.data.error, 'error');
      else toast('Save failed', 'error');
      throw e;
    }
  };

  const scrollH = (dir) => scrollRef.current?.scrollBy({ left: dir * 200, behavior: 'smooth' });
  const scrollV = (dir) => scrollRef.current?.scrollBy({ top: dir * 150, behavior: 'smooth' });

  // ── Filtering ────────────────────────────────────────────────────────────
  const totalCount  = candidates.length;
  const activeCount = candidates.filter(c => !c.closed).length;
  const topCount    = candidates.filter(c => c.score >= 4 && !c.closed).length;
  const contactedCount = candidates.filter(isContacted).length;
  const closedCount    = candidates.filter(isClosed).length;

  // KPI cards always count the full list, never the filtered rows.
  const toggleFilter = (f) => setFilter(filter === f ? 'ALL' : f);
  const kpiCards = [
    { key: 'total',     label: 'Total',          color: 'var(--primary)', count: totalCount,
      onClick: () => { setFilter('ALL'); setSearch(''); setScoreFilter(''); } },
    { key: 'active',    label: 'Active',         color: 'var(--success)', count: activeCount,
      active: filter === 'ACTIVE', onClick: () => toggleFilter('ACTIVE') },
    { key: 'top',       label: 'Top Candidates', color: 'var(--warning)', count: topCount,
      active: filter === 'TOP',    onClick: () => toggleFilter('TOP') },
    { key: 'contacted', label: 'Contacted',      color: '#A78BFA',        count: contactedCount,
      active: filter === 'CONTACTED', onClick: () => toggleFilter('CONTACTED') },
    { key: 'closed',    label: 'Closed',         color: '#7A8FAA',        count: closedCount,
      active: filter === 'CLOSED',    onClick: () => toggleFilter('CLOSED') },
  ];

  let filtered = candidates;
  if (filter === 'ACTIVE') filtered = filtered.filter(c => !c.closed);
  if (filter === 'TOP')    filtered = filtered.filter(c => c.score >= 4 && !c.closed);
  if (filter === 'CONTACTED') filtered = filtered.filter(isContacted);
  if (filter === 'CLOSED')    filtered = filtered.filter(isClosed);
  if (search) {
    filtered = filtered.filter(c => c.candidate_name?.toLowerCase().includes(search.toLowerCase()));
  }
  if (scoreFilter) {
    filtered = filtered.filter(c => c.score >= Number(scoreFilter));
  }

  // Keep the selection limited to rows that are currently visible and still exist.
  const visibleKey = filtered.map(c => c.id).join(',');
  useEffect(() => {
    setSelected(prev => {
      if (!prev.size) return prev;
      const visible = new Set(filtered.map(c => c.id));
      const next = new Set(Array.from(prev).filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleKey]);

  const selectedCount = filtered.filter(c => selected.has(c.id)).length;
  const allSelected   = filtered.length > 0 && selectedCount === filtered.length;

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map(c => c.id)));

  const handleBulkDelete = async () => {
    const ids = filtered.filter(c => selected.has(c.id)).map(c => c.id);
    if (!ids.length || bulkDeleting) return;
    if (!window.confirm(`Delete ${ids.length} candidate${ids.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    const results = await Promise.allSettled(ids.map(id => deleteCtoCandidate(id)));
    const deleted = new Set(ids.filter((_, i) => results[i].status === 'fulfilled'));
    const failed  = ids.length - deleted.size;
    if (deleted.size) {
      setCandidates(prev => prev.filter(c => !deleted.has(c.id)));
      setSelected(prev => new Set(Array.from(prev).filter(id => !deleted.has(id))));
      setEditId(prev => (prev !== null && deleted.has(prev) ? null : prev));
    }
    toast(failed ? `${deleted.size} deleted, ${failed} failed` : `✓ ${deleted.size} deleted`, failed ? 'error' : 'success');
    setBulkDeleting(false);
  };

  function renderCell(candidate, col) {
    if (col.type === 'action') {
      const action = getAction(candidate.score);
      if (!action) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
      return <span style={{ color: ACTION_COLORS[action], fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{action}</span>;
    }

    const rawVal = candidate[col.key];

    if (col.type === 'checkbox') {
      return rawVal
        ? <span style={{ color: 'var(--primary)', fontWeight: 700 }} aria-label="Yes">✓</span>
        : <span style={{ color: 'var(--text-dim)' }} aria-label="No">—</span>;
    }

    if (col.type === 'date') {
      const day = fmtDay(rawVal);
      return day ? <span style={{ whiteSpace: 'nowrap' }}>{day}</span> : <span style={{ color: 'var(--text-dim)' }}>—</span>;
    }

    if (col.type === 'link') {
      return rawVal
        ? <a href={rawVal} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--primary)', fontSize: 11 }}>View ↗</a>
        : <span style={{ color: 'var(--text-dim)' }}>—</span>;
    }

    if (col.type === 'score') {
      const suggested = getSuggestedScore(candidate);
      return (
        <div title={SCORE_TOOLTIP}>
          <span style={{ fontWeight: 600, color: rawVal ? getScoreColor(rawVal) : 'var(--text-dim)' }}>
            {rawVal || '—'}
          </span>
          {!rawVal && suggested && (
            <small style={{ display: 'block', fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>
              Suggested: {suggested}
            </small>
          )}
        </div>
      );
    }

    const wraps = col.type === 'text';
    return (
      <div
        style={wraps
          ? { color: rawVal || rawVal === 0 ? 'var(--text)' : 'var(--text-dim)', whiteSpace: 'normal', wordBreak: 'break-word' }
          : { color: rawVal || rawVal === 0 ? 'var(--text)' : 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}
      >
        {rawVal || rawVal === 0 ? rawVal : '—'}
      </div>
    );
  }


  const checkTh  = { padding: '8px 10px', textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, left: 0, zIndex: 4, width: 40, minWidth: 40 };
  const checkTd  = { padding: '8px 10px', textAlign: 'center', borderBottom: '1px solid var(--border)', position: 'sticky', left: 0, zIndex: 1, width: 40, minWidth: 40 };
  const frozenTh = { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, left: 40, zIndex: 3, width: 220, minWidth: 220 };
  const normalTh = { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-card)', position: 'sticky', top: 0, zIndex: 2 };
  const frozenTd = { padding: '8px 10px', borderBottom: '1px solid var(--border)', position: 'sticky', left: 40, zIndex: 1, fontWeight: 600, color: 'var(--text)', width: 220, minWidth: 220, maxWidth: 220 };
  const scrollBtn = { background: 'var(--bg-card2)', border: '1px solid var(--border-med)', borderRadius: 6, color: 'var(--text-muted)', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, flexShrink: 0 };

  const filterBtnStyle = (active) => ({
    background: active ? 'var(--primary-dim)' : 'var(--bg-card2)',
    border: `1px solid ${active ? 'var(--primary-border)' : 'var(--border-med)'}`,
    borderRadius: 'var(--radius-sm)',
    color: active ? 'var(--primary)' : 'var(--text-muted)',
    fontFamily: 'DM Sans, sans-serif', fontSize: 12, fontWeight: 600,
    padding: '6px 14px', cursor: 'pointer', whiteSpace: 'nowrap',
  });

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>;

  const editCandidate = editId !== null ? candidates.find(c => c.id === editId) : null;

  return (
    <>
    <KpiCards cards={kpiCards} columns={5} />
    <div className="pipeline-view" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Top bar */}
      <div className="cto-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexShrink: 0, minHeight: 32 }}>
        <span style={{ fontFamily: 'Sora, sans-serif', fontSize: 12, fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          CTO Candidate Evaluation
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {selectedCount > 0 && (
            <button className="bulk-del" onClick={handleBulkDelete} disabled={bulkDeleting} style={{ background: 'var(--danger-bg)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 'var(--radius-sm)', color: 'var(--danger)', fontFamily: 'DM Sans, sans-serif', fontSize: 12, fontWeight: 600, padding: '5px 14px', cursor: bulkDeleting ? 'not-allowed' : 'pointer', opacity: bulkDeleting ? 0.6 : 1, whiteSpace: 'nowrap' }}>
              {bulkDeleting ? 'Deleting…' : `🗑 Delete ${selectedCount} selected`}
            </button>
          )}
        </div>
      </div>

      {/* Quick add */}
      <form className="quick-add" onSubmit={handleCreate} style={{
        display: 'flex', gap: 8, marginBottom: 14, flexShrink: 0,
        background: 'var(--bg-card)',
        border: '1px solid var(--primary-border)',
        borderRadius: 'var(--radius)',
        padding: '10px 14px', alignItems: 'center',
      }}>
        <span className="quick-add-label" style={{
          fontFamily: 'Sora, sans-serif', fontSize: 12, fontWeight: 600,
          color: 'var(--primary)', whiteSpace: 'nowrap',
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
          Quick Add · CTO Candidates
        </span>
        <input
          type="text" value={newName}
          className="quick-add-input"
          onChange={e => setNewName(e.target.value)}
          placeholder="Candidate name…" aria-label="Candidate name"
          autoFocus={!isTouchDevice()}
          style={{
            flex: 1, background: 'var(--bg-card2)',
            border: '1px solid var(--border-med)',
            borderRadius: 'var(--radius-sm)', color: 'var(--text)',
            padding: '6px 12px', fontSize: 13,
            fontFamily: 'DM Sans, sans-serif', outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--primary)'}
          onBlur={e => e.target.style.borderColor = 'var(--border-med)'}
        />
        <button type="submit" className="quick-add-btn" disabled={!newName.trim() || creating} style={{
          background: newName.trim() && !creating
            ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))'
            : 'var(--bg-card2)',
          border: 'none', borderRadius: 'var(--radius-sm)',
          color: newName.trim() && !creating ? '#fff' : 'var(--text-dim)',
          fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13,
          padding: '6px 20px', cursor: newName.trim() && !creating ? 'pointer' : 'not-allowed',
          whiteSpace: 'nowrap',
        }}>
          {creating ? '…' : '+ Add'}
        </button>
      </form>

      {/* Controls bar */}
      <div className="controls-bar" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexShrink: 0, flexWrap: 'wrap' }}>
        <button style={filterBtnStyle(filter === 'ALL')}    onClick={() => setFilter('ALL')}>Total ({totalCount})</button>
        <button style={filterBtnStyle(filter === 'ACTIVE')} onClick={() => setFilter('ACTIVE')}>Active ({activeCount})</button>
        <button style={filterBtnStyle(filter === 'TOP')}    onClick={() => setFilter('TOP')}>Top Candidates ({topCount})</button>
        <button style={filterBtnStyle(filter === 'CONTACTED')} onClick={() => setFilter('CONTACTED')}>Contacted ({contactedCount})</button>
        <button style={filterBtnStyle(filter === 'CLOSED')}    onClick={() => setFilter('CLOSED')}>Closed ({closedCount})</button>
        <input
          placeholder="Search candidates…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, minWidth: 160, background: 'var(--bg-card2)',
            border: '1px solid var(--border-med)', borderRadius: 'var(--radius-sm)',
            color: 'var(--text)', padding: '6px 12px', fontSize: 13,
            fontFamily: 'DM Sans, sans-serif', outline: 'none',
          }}
        />
        <select
          value={scoreFilter}
          onChange={e => setScoreFilter(e.target.value)}
          style={{
            background: 'var(--bg-card2)', border: '1px solid var(--border-med)',
            borderRadius: 'var(--radius-sm)', color: 'var(--text)',
            padding: '6px 10px', fontSize: 13, fontFamily: 'DM Sans, sans-serif',
            outline: 'none', cursor: 'pointer',
          }}
        >
          <option value="">All Scores</option>
          <option value="5">5</option>
          <option value="4">4+</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', flex: 1, minHeight: 0 }}>
        {/* Scroll controls */}
        <div className="scroll-rail" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 6px', flexShrink: 0 }}>
          <button style={scrollBtn} onClick={() => scrollV(-1)}>▲</button>
          <button style={scrollBtn} onClick={() => scrollV(1)}>▼</button>
          <div style={{ width: 20, height: 1, background: 'var(--border)', margin: '2px 0' }} />
          <button style={scrollBtn} onClick={() => scrollH(-1)}>◀</button>
          <button style={scrollBtn} onClick={() => scrollH(1)}>▶</button>
        </div>

        {/* Table */}
        <div style={{ flex: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {!filtered.length ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, flex: 1 }}>
              {candidates.length ? 'No candidates match the current filters.' : 'No candidates yet. Use + Add Candidate to get started.'}
            </div>
          ) : (
            /* Single table — the Candidate Name column is frozen via CSS sticky
               positioning on the same <tr> as the rest of the row, so its height
               is always identical to the row's actual rendered height. There is
               no second table and no duplicated content to keep in sync. */
            <div ref={scrollRef} style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', position: 'relative', minWidth: 0 }}>
              <table className="touch-table cto-table" style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
                <thead>
                  <tr>
                    <th className="rt-check" style={checkTh}>
                      <label className="tap-area">
                        <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="Select all candidates" style={{ cursor: 'pointer', width: 14, height: 14, accentColor: 'var(--primary)' }} />
                      </label>
                    </th>
                    <th className="cto-frozen" style={frozenTh}>Candidate Name</th>
                    {COLUMNS.map(col => (
                      <th key={col.key} style={{ ...normalTh, ...getColumnWidth(col.key) }}>{col.label}</th>
                    ))}
                    <th style={{ ...normalTh, width: 50, minWidth: 50 }} />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, idx) => {
                    const isChecked = selected.has(c.id);
                    const baseBg = idx % 2 === 0 ? 'var(--bg-card)' : '#FFFFFF';
                    // Opaque (tint layered over the base colour) so sticky columns never show scrolled content through.
                    const rowBg = isChecked ? `linear-gradient(rgba(46,162,235,0.08), rgba(46,162,235,0.08)), ${baseBg}` : baseBg;
                    return (
                      <tr key={c.id}
                        onClick={() => setEditId(c.id)}
                        style={{ background: rowBg, opacity: c.closed ? 0.55 : 1, cursor: 'pointer' }}
                        onMouseEnter={e => { if (canHover()) Array.from(e.currentTarget.cells).forEach((cell, i) => { if (i > 1) cell.style.background = 'var(--bg-hover)'; }); }}
                        onMouseLeave={e => { if (canHover()) Array.from(e.currentTarget.cells).forEach((cell, i) => { if (i > 1) cell.style.background = ''; }); }}
                      >
                        <td className="rt-check" onClick={e => toggleSelect(c.id, e)}
                          style={{ ...checkTd, background: rowBg, borderLeft: isChecked ? '3px solid var(--primary)' : '3px solid transparent' }}>
                          <input type="checkbox" checked={isChecked} onChange={() => {}} onClick={e => toggleSelect(c.id, e)} aria-label={`Select ${c.candidate_name || 'candidate'}`} style={{ cursor: 'pointer', width: 14, height: 14, accentColor: 'var(--primary)' }} />
                        </td>
                        <td className="cto-frozen" style={{ ...frozenTd, background: rowBg }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.candidate_name || <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>Unnamed candidate</span>}
                          </div>
                        </td>
                        {COLUMNS.map(col => (
                          <td key={col.key} style={{
                            padding: '8px 10px',
                            borderBottom: '1px solid var(--border)',
                            whiteSpace: col.type === 'text' ? 'normal' : 'nowrap',
                            wordBreak: col.type === 'text' ? 'break-word' : 'normal',
                            overflow: col.type === 'text' ? 'visible' : 'hidden',
                            background: rowBg,
                            ...getColumnWidth(col.key),
                          }}>
                            {renderCell(c, col)}
                          </td>
                        ))}
                        <td style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'center', background: rowBg }}>
                          <button className="cto-del" onClick={e => { e.stopPropagation(); handleRowDelete(c.id); }} title="Delete candidate" aria-label="Delete candidate" style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14 }}>
                            🗑
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '7px 14px', fontSize: 11, color: 'var(--text-dim)', borderTop: '1px solid var(--border)', background: 'var(--bg-card)', flexShrink: 0 }}>
            {filtered.length} of {candidates.length} candidate{candidates.length !== 1 ? 's' : ''} shown{selectedCount > 0 && <span style={{ color: 'var(--primary)', marginLeft: 8 }}>· {selectedCount} selected</span>} · Click a row to open
          </div>
        </div>
      </div>
    </div>

    {editCandidate && (
      <CTOEditDrawer
        key={editCandidate.id}
        candidate={editCandidate}
        onSave={handleSave}
        onDelete={removeCandidate}
        onClose={() => setEditId(null)}
      />
    )}
    </>
  );
}
